/**
 * Contrato da API de configurações do tenant.
 * O front não cria chaves: `key`, `description` e `type` vêm do catálogo do backend.
 */

/** @typedef {'STRING' | 'BOOLEAN' | 'INTEGER'} ConfigurationType */

/**
 * @typedef {Object} SystemConfiguration
 * @property {string} key
 * @property {string} description
 * @property {ConfigurationType} type
 * @property {string} value Sempre string na API; interpretar conforme `type`.
 */

/**
 * @typedef {Object} UpdateConfigurationRequest
 * @property {string} value
 */

export const CONFIGURATION_TYPES = {
    STRING: 'STRING',
    BOOLEAN: 'BOOLEAN',
    INTEGER: 'INTEGER',
};

/** Única chave do catálogo atual. A tela de relatórios consulta só esta, individualmente. */
export const REPORT_MAX_PERIOD_DAYS_KEY = 'REPORT_MAX_PERIOD_DAYS';

/**
 * Faixas conhecidas, só como ajuda na UI. A validação definitiva é a resposta 400 da API.
 * @type {Record<string, { min: number, max: number }>}
 */
export const CONFIGURATION_INTEGER_RANGES = {
    [REPORT_MAX_PERIOD_DAYS_KEY]: { min: 1, max: 365 },
};

/** @type {SystemConfiguration} */
export const REPORT_MAX_PERIOD_DAYS_FIXTURE = {
    key: REPORT_MAX_PERIOD_DAYS_KEY,
    description: 'Limite de dias entre a data inicial e a data final na emissão de relatórios.',
    type: CONFIGURATION_TYPES.INTEGER,
    value: '90',
};

/**
 * @param {unknown} value
 * @returns {number | null}
 */
export function parseConfigurationInteger(value) {
    const raw = String(value ?? '').trim();
    if (!/^-?\d+$/.test(raw)) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isInteger(parsed) ? parsed : null;
}

/**
 * Valor editável inicial. Booleanos normalizam para "true" / "false".
 * @param {Pick<SystemConfiguration, 'type' | 'value'> | null | undefined} config
 * @returns {string}
 */
export function configurationDraftValue(config) {
    if (!config) return '';
    if (config.type === CONFIGURATION_TYPES.BOOLEAN) {
        return String(config.value).trim().toLowerCase() === 'true' ? 'true' : 'false';
    }
    return config.value == null ? '' : String(config.value);
}

/**
 * @param {Pick<SystemConfiguration, 'type' | 'value'> | null | undefined} config
 * @param {unknown} draft
 */
export function isSameConfigurationValue(config, draft) {
    if (!config) return false;
    if (config.type === CONFIGURATION_TYPES.BOOLEAN) {
        return configurationDraftValue(config) === configurationDraftValue({
            type: CONFIGURATION_TYPES.BOOLEAN,
            value: draft,
        });
    }
    return String(config.value ?? '') === String(draft ?? '');
}
